import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Subtitle } from "@/mocks/subtitles";
import { formatDownloads } from "@/utils/subtitles";

const heroImages: Record<string, string> = {
  "interstellar-2014":
    "https://readdy.ai/api/search-image?query=Abstract%20deep%20space%20scene%20with%20a%20giant%20subtle%20ringed%20planet%20and%20soft%20blue%20nebula%20clouds%2C%20cinematic%20dark%20tonal%20gradient%2C%20minimal%20high-contrast%20background%2C%20no%20text&width=1600&height=900&seq=hero-sub-01&orientation=landscape",
  "inception-2010":
    "https://readdy.ai/api/search-image?query=Abstract%20surreal%20city%20skyline%20folding%20into%20itself%20above%20dark%20reflective%20water%2C%20teal%20and%20amber%20gradient%20light%2C%20cinematic%20minimal%20background%2C%20no%20text&width=1600&height=900&seq=hero-sub-02&orientation=landscape",
  "farewell-my-concubine-1993":
    "https://readdy.ai/api/search-image?query=Abstract%20traditional%20Chinese%20opera%20stage%20with%20hanging%20red%20lanterns%20and%20warm%20golden%20light%20bokeh%2C%20deep%20crimson%20and%20charcoal%20gradient%2C%20cinematic%20minimal%20background%2C%20no%20text&width=1600&height=900&seq=hero-sub-03&orientation=landscape",
  "ne-zha-2-2025":
    "https://readdy.ai/api/search-image?query=Abstract%20swirling%20flames%20and%20ocean%20waves%20in%20vivid%20red%20and%20teal%2C%20dynamic%20ink-painting%20motion%2C%20cinematic%20dark%20gradient%20background%2C%20no%20text&width=1600&height=900&seq=hero-sub-04&orientation=landscape",
  "three-body-2023":
    "https://readdy.ai/api/search-image?query=Abstract%20celestial%20sphere%20with%20faint%20scanning%20rings%20above%20a%20dark%20city%20skyline%2C%20deep%20blue%20and%20crimson%20gradient%20light%2C%20cinematic%20minimal%20background%2C%20no%20text&width=1600&height=900&seq=hero-sub-05&orientation=landscape",
  "dune-part-two-2024":
    "https://readdy.ai/api/search-image?query=Abstract%20endless%20sand%20dunes%20under%20a%20pale%20amber%20sky%20with%20soft%20drifting%20dust%2C%20warm%20gold%20and%20olive%20gradient%2C%20cinematic%20minimal%20background%2C%20no%20text&width=1600&height=900&seq=hero-sub-06&orientation=landscape",
};

interface HeroCarouselProps {
  items: Subtitle[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const slides = items.slice(0, 4);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setActive((value) => (value + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;
  const current = slides[active];
  const image = heroImages[current.id] ?? current.cover;

  return (
    <section className="relative w-full">
      <div className="relative h-[480px] w-full overflow-hidden md:h-[600px]">
        <img
          src={image}
          alt={`${current.title} 高清中文字幕资源焦点图`}
          title={`${current.title} 中文字幕在线预览与下载`}
          className="h-full w-full object-cover transition-opacity duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/60 to-foreground-950/20"></div>

        <div className="absolute inset-0">
          <div className="mx-auto flex h-full w-full max-w-7xl flex-col justify-center px-4 md:px-6">
            <div className="w-full max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-primary-950">
                <i className="ri-fire-line text-sm"></i>
                本周焦点资源
              </span>
              <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
                {current.title}
              </h1>
              <p className="mt-2 text-base text-background-200 md:text-lg">{current.originalTitle}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-background-100 md:text-sm">
                <span className="rounded-md bg-background-50/15 px-2.5 py-1">{current.type}</span>
                <span className="rounded-md bg-background-50/15 px-2.5 py-1">{current.year}</span>
                <span className="rounded-md bg-background-50/15 px-2.5 py-1 uppercase">
                  {current.format}
                </span>
                <span className="rounded-md bg-background-50/15 px-2.5 py-1">{current.language}</span>
                <span className="flex items-center gap-1 rounded-md bg-background-50/15 px-2.5 py-1">
                  <i className="ri-download-2-line text-sm"></i>
                  {formatDownloads(current.downloads)}
                </span>
              </div>
              <p className="mt-3 hidden max-w-xl text-sm leading-relaxed text-background-200 sm:block">
                {current.summary}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={`/detail/${current.id}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  查看字幕详情
                </Link>
                <Link
                  to={`/category/${current.typeSlug}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50/10 px-5 py-2.5 text-sm font-semibold text-background-50 ring-1 ring-background-50/40 transition-colors hover:bg-background-50/20"
                >
                  <i className="ri-folder-2-line text-lg"></i>
                  浏览同类字幕
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 right-4 flex items-center gap-2 md:right-6">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`切换到 ${slide.title}`}
              onClick={() => setActive(index)}
              className={`h-1.5 rounded-full transition-all ${
                index === active ? "w-8 bg-accent-500" : "w-4 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => setActive((value) => (value - 1 + slides.length) % slides.length)}
          className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/15 text-background-50 ring-1 ring-background-50/30 transition-colors hover:bg-background-50/30 md:flex"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => setActive((value) => (value + 1) % slides.length)}
          className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/15 text-background-50 ring-1 ring-background-50/30 transition-colors hover:bg-background-50/30 md:flex"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>
      </div>
    </section>
  );
}